import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import '../../styles/csr.css';
// eslint-disable-next-line import/extensions
import { RiDeleteBack2Fill } from 'react-icons/ri';

const RequirementKanban = ({ tickets, onScoreUpdate, onDelete, loading }) => {
    const columnOrder = [
        'Pending Review',
        'Requires Immediate Attention',
        'Operational with Limitations',
        'Optimized for Productivity'
    ];

    const scoreMap = {
        'Requires Immediate Attention': 1,
        'Operational with Limitations': 2,
        'Optimized for Productivity': 3
    };
    const [columns, setColumns] = useState({
        'Pending Review': [],
        'Requires Immediate Attention': [],
        'Operational with Limitations': [],
        'Optimized for Productivity': []
    });

    // Initialize columns with tickets when component mounts or when tickets change
    useEffect(() => {
        const updatedColumns = {
            'Pending Review': [],
            'Requires Immediate Attention': [],
            'Operational with Limitations': [],
            'Optimized for Productivity': []
        };
        tickets.forEach((ticket) => {
            // Sort tickets into their corresponding columns based on the score
            if (ticket.score === 0) {
                updatedColumns['Pending Review'].push(ticket);
            } else if (ticket.score === 1) {
                updatedColumns['Requires Immediate Attention'].push(ticket);
            } else if (ticket.score === 2) {
                updatedColumns['Operational with Limitations'].push(ticket);
            } else if (ticket.score === 3) {
                updatedColumns['Optimized for Productivity'].push(ticket);
            }
        });

        setColumns(updatedColumns);
    }, [tickets]); // Runs every time tickets prop changes

    // Handle ticket drop event
    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;

        const sourceColumn = source.droppableId;
        const destinationColumn = destination.droppableId;
        // If ticket is moved to the same column, no need to do anything
        if (sourceColumn === destinationColumn) return;

        // Move ticket to new column
        const newColumns = { ...columns };
        const ticket = columns[sourceColumn].find(
            (item) => item.id === draggableId
        );

        // Remove the ticket from the source column
        newColumns[sourceColumn] = columns[sourceColumn].filter(
            (item) => item.id !== draggableId
        );

        // Add the ticket to the destination column
        newColumns[destinationColumn].push(ticket);

        setColumns(newColumns);

        // Update score in the parent component (this will trigger the necessary updates in the backend)
        const newScore = scoreMap[destinationColumn] || 0;

        console.log(
            `Ticket with ID: ${ticket.id} moved from ${sourceColumn} to ${destinationColumn}`
        );
        console.log(`Updated score for ticket: ${newScore}`);

        // Update score in backend based on destination column
        onScoreUpdate(ticket.id, newScore);
    };
    const handleDelete = (ticketId) => {
        // eslint-disable-next-line no-alert
        if (window.confirm('Are you sure you want to delete this ticket?')) {
            onDelete(ticketId);
        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="kanban-board">
                {columnOrder.map((column) => (
                    <Droppable droppableId={column} key={column}>
                        {(dropProvided) => (
                            <div
                                className="kanban-column"
                                ref={dropProvided.innerRef}
                                {...dropProvided.droppableProps}>
                                <h4>
                                    {column}
                                    {scoreMap[column] && (
                                        <div className="score-label">
                                            Score - {scoreMap[column]}
                                        </div>
                                    )}
                                </h4>
                                {columns[column].map((ticket, index) => (
                                    <Draggable
                                        key={ticket.id}
                                        draggableId={ticket.id}
                                        index={index}
                                        isDragDisabled={loading.update}>
                                        {(dragProvided) => (
                                            <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                {...dragProvided.dragHandleProps}
                                                className="ticket">
                                                <div className="ticket-content">
                                                    {ticket.category} {' : '}
                                                    {ticket.details}
                                                    <button
                                                        className="delete-button"
                                                        onClick={() =>
                                                            handleDelete(
                                                                ticket.id
                                                            )
                                                        }
                                                        disabled={
                                                            loading.delete
                                                        }>
                                                        <RiDeleteBack2Fill />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {dropProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                ))}
            </div>
        </DragDropContext>
    );
};

RequirementKanban.propTypes = {
    tickets: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            details: PropTypes.string.isRequired,
            score: PropTypes.number.isRequired
        })
    ).isRequired,
    onScoreUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    loading: PropTypes.shape({
        create: PropTypes.bool,
        update: PropTypes.bool,
        delete: PropTypes.bool
    }).isRequired
};
export default RequirementKanban;
